/**
 * Unit 1.4 · IMPL.E: the rebuilt Clients list page (shadcn, zero MUI).
 *
 * Bead: insurescope-11zkc.2.4.3.5. Thin shell over `useClientListTable`
 * (fat hook): stats strip → controls row (search + 5-option Sort dropdown +
 * Add Client) → table with interactive rows (the whole row opens the client
 * workspace; the kebab is the only non-navigating zone) → designed
 * empty/error states → standard pagination. Sorting is dropdown-driven;
 * column headers are static. Design contract:
 * docs/design-briefs/2026-06-03-clients-list.md.
 */
import { getLibraryFolders } from "@/api";
import { DocumentDropZone } from "@/components/frontend/DocumentDropZone";
import { FrontendPageContent } from "@/components/frontend/FrontendPageContent";
import { DataTablePagination } from "@/components/shared/DataTablePagination";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { HOME_ORG_PATH } from "@/config/constants";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { cn } from "@/lib/utils";
import { useDebouncedState } from "@react-hookz/web";
import { useQuery } from "@tanstack/react-query";
import { flexRender } from "@tanstack/react-table";
import { ArrowUpDown, Check, Search, UserPlus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { IClient } from "@/api";

import { UploadDialog } from "../documents/components/UploadDialog";
import { ClientFormDialog } from "./components/ClientFormDialog";
import {
    ClientListEmptyLibrary,
    ClientListEmptySearch,
    ClientListError,
} from "./components/ClientListEmptyState";
import { ClientListSkeleton } from "./components/ClientListSkeleton";
import { ClientRowActions } from "./components/ClientRowActions";
import { ClientStatsHeader } from "./components/ClientStatsHeader";
import {
    CLIENT_SORT_OPTIONS,
    useClientListTable,
} from "./hooks/useClientListTable";

// Module scope on purpose: a stable identity keeps the hook's columns memo
// (and the table instance) from churning every render.
const renderActions = (client: IClient) => <ClientRowActions client={client} />;

const OrganizationClients = () => {
    const { setBreadcrumbs } = useBreadcrumb();
    useDocumentTitle("Clients");

    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useDebouncedState("", 300);
    const [addOpen, setAddOpen] = useState(false);
    const searchRef = useRef<HTMLInputElement | null>(null);

    // Shared cache with the library page; no extra fetch when both are warm.
    const { data: foldersData } = useQuery({
        queryKey: ["frontend-library-folders"],
        queryFn: () => getLibraryFolders(),
    });
    const folders = foldersData?.folders ?? [];

    const [uploadOpen, setUploadOpen] = useState(false);
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);

    const handleDropFiles = (files: File[]) => {
        setDroppedFiles(files);
        setUploadOpen(true);
    };

    useEffect(() => {
        setBreadcrumbs([
            { label: "Home", path: HOME_ORG_PATH },
            { label: "Clients" },
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
    }, []);

    // `/` focuses the search from anywhere on the page (unless another
    // input/dialog already has focus).
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "/") return;
            const active = document.activeElement;
            if (
                active instanceof HTMLElement &&
                (active.tagName === "INPUT" ||
                    active.tagName === "TEXTAREA" ||
                    active.isContentEditable)
            ) {
                return;
            }
            e.preventDefault();
            searchRef.current?.focus();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    const {
        table,
        branch,
        rows,
        isFetching,
        sortKey,
        setSortKey,
        refetch,
        handleRowClick,
        handleRowKeyDown,
    } = useClientListTable({ searchQuery, renderActions });

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setSearchQuery(value);
    };

    const clearSearch = useCallback(() => {
        setSearchInput("");
        setSearchQuery("");
    }, [setSearchQuery]);

    const sortLabel =
        CLIENT_SORT_OPTIONS.find((o) => o.key === sortKey)?.label ??
        "Recently active";

    return (
        <DocumentDropZone
            title="Upload PDF or spreadsheet files here"
            subtitle="We'll automatically match them to your existing clients"
            onDropFiles={handleDropFiles}
            disabled={uploadOpen}
            allowSpreadsheets
            // The wrapper fills the page slot and must NOT scroll: the overlay
            // is anchored to it, so scrolling stays inside FrontendPageContent
            // or a scrolled list would carry the overlay out of view.
            className="flex min-h-0 flex-1 flex-col"
        >
            <FrontendPageContent className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
                {/* Page header: eyebrow + title + stats, Add Client at the right. */}
                <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-col gap-1">
                        <h1 className="text-primary text-2xl font-bold tracking-tight">
                            Clients
                        </h1>
                        <ClientStatsHeader />
                    </div>
                    <Button onClick={() => setAddOpen(true)}>
                        <UserPlus className="size-4" />
                        Add Client
                    </Button>
                </div>

                {/* Controls row: search (/-focusable) + dropdown-driven sort. */}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative max-w-sm flex-1">
                        <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
                        <Input
                            ref={searchRef}
                            value={searchInput}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            placeholder="Search clients by name or industry"
                            className="pr-8 pl-8"
                        />
                        <kbd className="bg-muted text-muted-foreground pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 rounded border px-1 text-[10px]">
                            /
                        </kbd>
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            render={
                                <Button variant="outline">
                                    <ArrowUpDown className="size-4" />
                                    Sort: {sortLabel}
                                </Button>
                            }
                        />
                        <DropdownMenuContent align="end">
                            {CLIENT_SORT_OPTIONS.map((option) => (
                                <DropdownMenuItem
                                    key={option.key}
                                    onClick={() => setSortKey(option.key)}
                                >
                                    <span className="flex-1">
                                        {option.label}
                                    </span>
                                    {option.key === sortKey && (
                                        <Check className="size-4" />
                                    )}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Document-library frame parity (Unit 4, parity brief
                docs/design-briefs/2026-06-05-clients-table-parity.md).
                State container (D1): data-density drives --td-py cell
                rhythm (D5); any in-flight fetch fades the zone to the
                shipped canon's opacity-75 (D7); aria-busy only on the
                initial load: branch === "loading" is the hook's
                isPending, equivalent to the canon's `isFetching && !data`
                precisely because keepPreviousData keeps `data` defined
                across later fetches (revisit if placeholderData is ever
                removed from useClientListTable; 2.1.6 review note). */}
                <div
                    data-density="standard"
                    aria-busy={branch === "loading" ? true : undefined}
                    className={cn(
                        "flex flex-col transition-opacity duration-150",
                        "[&_[data-slot=table-cell]]:py-[var(--td-py,0.875rem)]",
                        isFetching && "opacity-75"
                    )}
                >
                    {/* Bordered frame (D2): holds EITHER the table or a
                    StateShell (D9) -- empty/error never get an orphaned
                    header band above them. */}
                    <div className="overflow-hidden rounded-md border">
                        {branch === "error" ? (
                            <ClientListError onRetry={() => refetch()} />
                        ) : branch === "empty-library" ? (
                            <ClientListEmptyLibrary
                                onAddClient={() => setAddOpen(true)}
                            />
                        ) : branch === "empty-search" ? (
                            <ClientListEmptySearch
                                // The DEBOUNCED value; the branch derives from
                                // it, so headline and branch always agree (no
                                // clearing flash).
                                query={searchQuery}
                                onClearSearch={clearSearch}
                            />
                        ) : (
                            /* RD1 ruled (insurescope-eltbv): v1's phantom-
                           scrollbar override DELETED: in this placement
                           it was structurally inert (className lands on
                           the inner <table>; a descendant selector can't
                           reach its parent [data-slot=table-container]).
                           Scroll duty stays on the primitive's container,
                           exactly the documents canon. Read the migration
                           playbook's Pattern 10 before reintroducing any
                           override here. */
                            <Table>
                                <TableHeader className="bg-muted/40">
                                    {table
                                        .getHeaderGroups()
                                        .map((headerGroup) => (
                                            <TableRow key={headerGroup.id}>
                                                {headerGroup.headers.map(
                                                    (header) => (
                                                        <TableHead
                                                            key={header.id}
                                                            style={{
                                                                width: header.getSize(),
                                                            }}
                                                            className="text-muted-foreground font-medium"
                                                        >
                                                            {flexRender(
                                                                header.column
                                                                    .columnDef
                                                                    .header,
                                                                header.getContext()
                                                            )}
                                                        </TableHead>
                                                    )
                                                )}
                                            </TableRow>
                                        ))}
                                </TableHeader>
                                {branch === "loading" ? (
                                    <ClientListSkeleton />
                                ) : (
                                    <TableBody>
                                        {table.getRowModel().rows.map((row) => (
                                            <TableRow
                                                key={row.id}
                                                tabIndex={0}
                                                aria-label={row.original.name}
                                                onClick={handleRowClick(
                                                    row.original
                                                )}
                                                onKeyDown={handleRowKeyDown(
                                                    row.original
                                                )}
                                                // hover/transition come from the
                                                // TableRow primitive (D6).
                                                className="cursor-pointer"
                                            >
                                                {row
                                                    .getVisibleCells()
                                                    .map((cell) => (
                                                        <TableCell
                                                            key={cell.id}
                                                        >
                                                            {flexRender(
                                                                cell.column
                                                                    .columnDef
                                                                    .cell,
                                                                cell.getContext()
                                                            )}
                                                        </TableCell>
                                                    ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                )}
                            </Table>
                        )}
                    </div>
                    {/* Pagination strip (D8): the SHIPPED canon position --
                    below the frame, inside this container, py-2. Gated to
                    populated (RD3: no dead chrome under empty states). */}
                    {branch === "populated" && rows.length > 0 && (
                        <div className="py-2">
                            <DataTablePagination
                                table={table}
                                entityLabel="clients"
                            />
                        </div>
                    )}
                </div>

                <ClientFormDialog
                    open={addOpen}
                    onOpenChange={setAddOpen}
                    client={null}
                />
                <UploadDialog
                    open={uploadOpen}
                    onClose={() => {
                        setUploadOpen(false);
                        setDroppedFiles([]);
                    }}
                    folderId={null}
                    isKnowledgeBase={false}
                    folders={folders}
                    allowSpreadsheets
                    initialFiles={
                        droppedFiles.length > 0 ? droppedFiles : undefined
                    }
                    onUploadComplete={() => {}}
                />
            </FrontendPageContent>
        </DocumentDropZone>
    );
};

export default OrganizationClients;
